import {localizeURL, redirectToURL, useDefault} from './common';
import {IAppConfig} from '../store/interfaces/IAppConfig';
import {Reservation} from '../api/model/reservation/reservation.model';
import {Hotel} from '../api/model/hotel/hotel.model';
import {URLSearchParams} from '@angular/http';
import {DateUtils} from './date.utils';
import {DateParam} from './model/date-param.model';

export enum SiteURL {
  CONTENT_NOT_FOUND,
  ACCOUNT_MANAGEMENT,
  CUSTOMER_CARE_WORLD_WIDE,
  HOMEPAGE_ERROR,
  MANAGE_YOUR_STAY,
  HOMEPAGE,
  TRANSLATIONS,
  HOTEL_DETAILS,
  HOTEL_REVIEWS,
  RESERVATION_CHANGE_CONFIRMATION,
  CHANGE_ROOM,
  CHANGE_RATE,
  HOMEPAGE_6C,
  SIGN_IN,
  ENROLLMENT,
  RESERVATION_VIEW,
  RESERVATION_CONFIRMATION,
  TRIP_EXTRAS,
  STAY_EVENTS,
  TERMS_CONDITIONS,
  AMB_JOIN,
  APP_GET,
  CUSTOMER_CARE
}
const QUERY_STRING = '?';
export class SiteURLUtils {
  private static APPLICATION_URLS: Map<SiteURL, string> = new Map<SiteURL, string>([
    [SiteURL.CONTENT_NOT_FOUND , '{/hotelBrand}/content/{country}/{language}/404'],
    [SiteURL.ACCOUNT_MANAGEMENT , '/rewardsclub/{country}/{language}/account-mgmt/home'],
    [SiteURL.CUSTOMER_CARE_WORLD_WIDE , '{/hotelBrand}/hotels/{country}/{language}/customer-care/worldwide-reservation-offices'],
    [SiteURL.HOMEPAGE_ERROR, '{/hotelBrand}/hotels/{country}/{language}/reservation?error=sue'],
    [SiteURL.MANAGE_YOUR_STAY, '{/hotelBrand}/hotels/{country}/{language}/ManageYourStay'],
    [SiteURL.HOMEPAGE, '{/hotelBrand}/hotels/{country}/{language}/reservation'],
    [SiteURL.TRANSLATIONS, '/resources/{country}/{language}/stay-mgmt/v12.0.0/translations/translations.json'],
    [SiteURL.HOTEL_DETAILS, '{/hotelBrand}/hotels/{country}/{language}/{seoCity}/{mnemonic}/hoteldetail'],
    [SiteURL.HOTEL_REVIEWS, '/hotels/{country}/{language}/{seoCity}/{mnemonic}/hoteldetail/hotel-reviews'],
    [SiteURL.CHANGE_ROOM, '{/hotelBrand}/hotels/{country}/{language}/find-hotels/hotel/rooms/change'],
    [SiteURL.CHANGE_RATE, '{/hotelBrand}/hotels/{country}/{language}/find-hotels/hotel/rates/change'],
    [SiteURL.RESERVATION_CHANGE_CONFIRMATION, '{/hotelBrand}/hotels/{country}/{language}/pay/change-confirmation?confirmationNumber={confirmationNumber}&lastName={lastName}'],
    [SiteURL.HOMEPAGE_6C, '/hotels/{country}/{language}/reservation'],
    [SiteURL.SIGN_IN, '/rewardsclub/{country}/{language}/sign-in/'],
    [SiteURL.ENROLLMENT, '/rewardsclub/{country}/{language}/join'],
    [SiteURL.RESERVATION_VIEW, '{/hotelBrand}/hotels/{country}/{language}/pay/reservation-view'],
    [SiteURL.RESERVATION_CONFIRMATION, '{/hotelBrand}/hotels/{country}/{language}/pay/confirmation?confirmationNumber={confirmationNumber}&lastName={lastName}'],
    [SiteURL.TRIP_EXTRAS, '/trip-extras/{country}/{language}/car/manage'],
    [SiteURL.STAY_EVENTS, '/rewardsclub/{country}/{language}/account-mgmt/staysevents'],
    [SiteURL.TERMS_CONDITIONS, '/hotels/{country}/{language}/global/customer_care/member-tc'],
    [SiteURL.AMB_JOIN, '/intercontinental/hotels/{country}/{language}/ambassador/join?offerId=WBJIC'],
    [SiteURL.APP_GET, '/content/{country}/{language}/support/mobile?scmisc=OSMAM-HI-US-EN-App_Get'],
    [SiteURL.CUSTOMER_CARE, '/hotels/{country}/{language}/customer-care/']
  ]);

  static getLocalizedSiteUrl(appConfig: IAppConfig, key = SiteURL.HOMEPAGE): string {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(key), appConfig);
  }

  static getLocalizedChangeRoomUrl(appConfig: IAppConfig, reservation: Reservation, hotel: Hotel, removeGrpCd?: boolean, groupCode?: string): string {
    return SiteURLUtils.getLocalizedSiteUrl(appConfig, SiteURL.CHANGE_ROOM) + QUERY_STRING + SiteURLUtils.buildFindHotelsParameters(reservation, hotel, removeGrpCd, groupCode).toString();
  }

  static getLocalizedChangeRateUrl(appConfig: IAppConfig, reservation: Reservation, hotel: Hotel): string {
    return SiteURLUtils.getLocalizedSiteUrl(appConfig, SiteURL.CHANGE_RATE) + QUERY_STRING + SiteURLUtils.buildFindHotelsParameters(reservation, hotel).toString();
  }

  static redirectToSiteUrl(appConfig: IAppConfig, key = SiteURL.HOMEPAGE) {
    redirectToURL(SiteURLUtils.getLocalizedSiteUrl(appConfig, key));
  }

  static redirectToReservationChangeConfirmation(appConfig: IAppConfig, confirmationNumber: string, lastName: string) {
    redirectToURL(SiteURLUtils.getLocalizedReservationChangeConfirmationUrl(appConfig, confirmationNumber, lastName));
  }

  static redirectToHotelDetails(appConfig: IAppConfig, mnemonic: string, seoCity: string) {
    redirectToURL(SiteURLUtils.getLocalizedHotelDetailsUrl(appConfig, mnemonic, seoCity));
  }

  static redirectToReservationView(appConfig: IAppConfig, confirmationNumber: string, lastName: string) {
    redirectToURL(this.getLocalizedReservationViewUrl(appConfig, confirmationNumber, lastName));
  }

  static redirectToReservationConfirmation(appConfig: IAppConfig, confirmationNumber: string, lastName: string) {
    redirectToURL(this.getLocalizedReservationConfirmationUrl(appConfig, confirmationNumber, lastName));
  }

  static redirectToStaysEvents(appConfig: IAppConfig) {
    redirectToURL(this.getLocalizedStaysEventsUrl(appConfig));
  }

  static redirectToAmbassadorJoin(appConfig: IAppConfig) {
    redirectToURL(this.getLocalizedAmbassadorJoinUrl(appConfig));
  }

  static getLocalizedHotelDetailsUrl(appConfig: IAppConfig, mnemonic: string, seoCity?: string, brandCode?: string) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.HOTEL_DETAILS).replace('{/hotelBrand}', (brandCode == 'MRMS' ? '/mrandmrssmith' : '')).replace('{mnemonic}', useDefault(mnemonic, '').toLowerCase()).replace('{seoCity}', useDefault(seoCity, '').toLowerCase()).replace(/\/{2,}/g,  '/'), appConfig);
  }

  static getLocalizedHotelReviewsUrl(appConfig: IAppConfig, mnemonic: string, seoCity?: string) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.HOTEL_REVIEWS).replace('{mnemonic}', useDefault(mnemonic, '').toLowerCase()).replace('{seoCity}', useDefault(seoCity, '').toLowerCase()).replace(/\/{2,}/g,  '/'), appConfig);
  }

  static getLocalizedReservationChangeConfirmationUrl(appConfig: IAppConfig, confirmationNumber: string, lastName: string) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.RESERVATION_CHANGE_CONFIRMATION).replace('{confirmationNumber}', useDefault(confirmationNumber, '')).replace('{lastName}', useDefault(encodeURIComponent(lastName), '')), appConfig);
  }

  static getLocalizedReservationViewUrl(appConfig: IAppConfig, confirmationNumber: string, lastName: string) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.RESERVATION_VIEW), appConfig) + QUERY_STRING + SiteURLUtils.buildReservationParameters(confirmationNumber, lastName);
  }

  static getLocalizedReservationConfirmationUrl(appConfig: IAppConfig, confirmationNumber: string, lastName: string) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.RESERVATION_CONFIRMATION).replace('{confirmationNumber}', useDefault(confirmationNumber, '')).replace('{lastName}', useDefault(lastName, '')), appConfig);
  }

  static getLocalizedStaysEventsUrl(appConfig: IAppConfig) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.STAY_EVENTS), appConfig);
  }

  static getLocalizedTermsConditionsUrl(appConfig: IAppConfig) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.TERMS_CONDITIONS), appConfig);
  }

  static getLocalizedAmbassadorJoinUrl(appConfig: IAppConfig) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.AMB_JOIN), appConfig);
  }
  static getLocalizedCustomerCareUrl(appConfig: IAppConfig) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.CUSTOMER_CARE), appConfig);
  }
  static getLocalizedAppGetUrl(appConfig: IAppConfig) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.APP_GET), appConfig);
  }
  static getLocalizedAccountManagementUrl(appConfig: IAppConfig) {
    return localizeURL(SiteURLUtils.APPLICATION_URLS.get(SiteURL.ACCOUNT_MANAGEMENT), appConfig);
  }


  private static buildFindHotelsParameters(reservation: Reservation, hotel: Hotel, removeGrpCd?: boolean, groupCode?: string): URLSearchParams {
    const params: URLSearchParams = new URLSearchParams();
    params.set('qDest', [hotel.address.city, hotel.address.stateName, hotel.address.countryName].filter(item => !!item).join(', '));
    params.set('qCiD', String(reservation.stay.checkInDate.getDate()));
    params.set('qCiMy', this._getMonthYear(reservation.stay.checkInDate));
    params.set('qCoD', String(reservation.stay.checkOutDate.getDate()));
    params.set('qCoMy', this._getMonthYear(reservation.stay.checkOutDate));
    params.set('qAdlt', String(reservation.stay.numberOfAdults));
    params.set('qChld', String(reservation.stay.numberOfChildren));
    params.set('qRms', String(reservation.stay.numberOfRooms));
    params.set('qRtP', reservation.rate.code);
    params.set('qSlRc', reservation.room.code);
    params.set('qSlH', hotel.mnemonic);
    params.set('qCN', reservation.confirmationNumber);
    params.set('qLN', reservation.guest.lastName);
    if (reservation.rate.groupCode && !removeGrpCd) {
      params.set('qGrpCd', reservation.rate.groupCode);
    } else if (groupCode) {
      params.set('qGrpCd', groupCode);
    }
    return params;
  }
  private static buildReservationParameters(confirmationNumber: string, lastName: string) {
    const params: URLSearchParams = new URLSearchParams();
    params.set('confirmationNumber', confirmationNumber);
    params.set('lastName', lastName);
    return params;
  }

  private static _getMonthYear(date: Date): string {
    return String(date.getMonth()) + String(date.getFullYear());
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/common/site.url.utils.ts